import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Check, Download, Eye, Trash2, X } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import { generateEvaluationReport } from '../../utils/reportGenerator';
import './Evaluation.css';

const RATING_SCALE = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" }
];

const RATING_DESCRIPTIONS = {
  1: "Unsatisfactory",
  2: "Regularly below expectation",
  3: "Average",
  4: "Satisfactory",
  5: "Regularly exceeds expectation"
};

const LEVEL_NAMES = [
  'Entry Level',
  'Operational Excellence',
  'Functional Accountability',
  'Growth & Sustainability',
];

// Determine whether an evaluation_type string maps to Core competency tables
const isCoreEvalType = (type) => type?.toLowerCase().includes('core');

// Parse a responsibility field that may be a raw DB string with escape chars,
// a JSON-encoded array, or already an array.
const parseResponsibilities = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (!resp) return [];
  if (typeof resp === 'string') {
    try {
      const parsed = JSON.parse(resp);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return resp
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, ' ')
      .split('\n')
      .map(line => line.replace(/^[-\s\d.]+/, '').trim())
      .filter(Boolean);
  }
  return [];
};

// Unescape a plain text field that may contain literal \n / \t sequences
const parseText = (text) => {
  if (!text) return '';
  return String(text).replace(/\\n/g, '\n').replace(/\\t/g, ' ');
};

// Static role information fallback for People and Culture department
const PEOPLE_CULTURE_ROLES = {
  'Senior Manager, HR Business Partner': {
    purpose: 'The Senior Manager, Human Resources Business Partner (HRBP) supports in-country operations to achieve the organization\'s People and Culture performance objectives at the country level and serves as a strategic business partner in driving and coordinating performance objectives aligned with the organization\'s overall strategy.',
    responsibilities: [
      'Support the Country Director in cascading the organization\'s global strategy across all local functions',
      'Serve as the primary liaison between the global office and the local office for all administrative and operational support needs',
      'Implement the organization\'s global HR strategy at the country level',
      'Coordinate and support local and international sourcing, recruitment, and onboarding of in-country staff',
      'Ensure full compliance with local legal, regulatory, and statutory requirements',
      'Strengthen the organization\'s positioning as an employer of choice in the local market'
    ]
  },
  'Director, Talent and Capability Management': {
    purpose: 'The Director of Talent and Capability Management leads the strategic development and implementation of talent acquisition, development, and retention programs across the organization.',
    responsibilities: [
      'Develop and implement comprehensive talent management strategy',
      'Oversee talent acquisition and recruitment processes',
      'Design and execute leadership development programs',
      'Manage succession planning and career development frameworks',
      'Drive organizational capability building initiatives',
      'Partner with senior leadership on workforce planning',
      'Establish performance management systems and processes',
      'Lead change management initiatives related to people and culture'
    ]
  },
  'Talent Acquisition Specialist': {
    purpose: 'The Talent Acquisition Specialist is responsible for identifying, attracting, and hiring qualified candidates to meet the organization\'s workforce needs.',
    responsibilities: [
      'Source and identify qualified candidates through various channels',
      'Conduct initial screening and interviews',
      'Coordinate interview schedules with hiring managers',
      'Manage job postings and recruitment advertising',
      'Build and maintain talent pipelines',
      'Track recruitment metrics and provide regular reports',
      'Ensure compliance with hiring policies and procedures',
      'Support employer branding initiatives'
    ]
  },
  'Learning and Development Coordinator': {
    purpose: 'The Learning and Development Coordinator supports the design, coordination, and delivery of training and development programs that enhance employee skills, knowledge, and performance.',
    responsibilities: [
      'Coordinate training programs and workshops',
      'Manage learning management system and training records',
      'Support development of training materials and resources',
      'Schedule and organize learning events',
      'Track training completion and effectiveness',
      'Assist in identifying training needs',
      'Coordinate with external training providers',
      'Support onboarding and orientation programs'
    ]
  },
  'HR Administrator': {
    purpose: 'The HR Administrator provides essential administrative support to the People and Culture team, ensuring smooth day-to-day operations and maintaining accurate employee records.',
    responsibilities: [
      'Maintain employee records and HR databases',
      'Process HR documentation and correspondence',
      'Support payroll processing and benefits administration',
      'Coordinate employee onboarding paperwork',
      'Respond to routine employee inquiries',
      'Schedule meetings and maintain calendars',
      'Prepare HR reports and presentations',
      'Maintain confidentiality of sensitive information'
    ]
  },
  'People and Culture Manager': {
    purpose: 'The People and Culture Manager oversees the implementation of HR policies, programs, and practices that support employee engagement, performance, and organizational culture.',
    responsibilities: [
      'Implement HR policies and procedures',
      'Manage employee relations and resolve workplace issues',
      'Oversee performance management processes',
      'Lead employee engagement initiatives',
      'Coordinate with department heads on HR matters',
      'Ensure compliance with labor laws and regulations',
      'Manage HR budget and resource allocation',
      'Support organizational development initiatives'
    ]
  }
};

// ─── Supabase API helpers ─────────────────────────────────────────────────────
const API = {
  async getEvaluations(organizationId) {
    try {
      let query = supabase
        .from('evaluations')
        .select(`*, department:department_id(depart_name), role:role_id(role_title)`)
        .order('created_at', { ascending: false });
      if (organizationId) query = query.eq('organization_id', organizationId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      return [];
    }
  },

  async getDepartments(organizationId) {
    try {
      let query = supabase.from('department').select('*').order('depart_name');
      if (organizationId) query = query.eq('organization_id', organizationId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  },

  async getCategories(organizationId) {
    try {
      const { data, error } = await supabase
        .from('competency_category')
        .select('category_id, category_name')
        .eq('organization_id', organizationId)
        .order('created_at');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  async getRolesByDepartment(departmentId) {
    try {
      const { data, error } = await supabase
        .from('roles').select('*').eq('department_id', departmentId).order('role_title');
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  },

  async getRoleById(roleId) {
    try {
      const { data, error } = await supabase
        .from('roles').select('*').eq('role_id', roleId).single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching role details:', error);
      return null;
    }
  },

  // Loads the full hierarchy: groups → competencies → level descriptors.
  // groupType: 'Core' uses corp_ tables; anything else uses func_ tables.
  async getCompetencyData(groupType, organizationId, departmentId) {
    try {
      const isCore = groupType === 'Core';

      // ── Step 1: load groups with their competency items ───────────────────
      let groups = [];
      if (isCore) {
        const { data, error } = await supabase
          .from('corp_competence_groups')
          .select(`
            group_id, group_name, weighting,
            corp_competence_items(competency_id, competency_name)
          `)
          .eq('organization_id', organizationId)
          .order('created_at');
        if (error) throw error;
        groups = (data || []).map(g => ({
          group_id: g.group_id,
          group_name: g.group_name,
          competencies: (g.corp_competence_items || []).map(c => ({
            competency_id: c.competency_id,
            competency_name: c.competency_name,
          })),
        }));
      } else {
        if (!departmentId) return [];
        const { data, error } = await supabase
          .from('func_competence_groups')
          .select(`
            group_id, group_name, group_weight,
            func_competence_items(item_id, item_name)
          `)
          .eq('department_id', departmentId)
          .eq('organization_id', organizationId)
          .order('created_at');
        if (error) throw error;
        groups = (data || []).map(g => ({
          group_id: g.group_id,
          group_name: g.group_name,
          competencies: (g.func_competence_items || []).map(c => ({
            competency_id: c.item_id,
            competency_name: c.item_name,
          })),
        }));
      }

      groups = groups.filter(g => g.competencies.length > 0);
      if (groups.length === 0) return [];

      // ── Step 2: load descriptors from competency_descriptions ─────────────
      const allCompIds = groups.flatMap(g => g.competencies.map(c => c.competency_id));
      const descriptorMap = {};

      if (allCompIds.length > 0) {
        const { data: descRows, error: descErr } = await supabase
          .from('competency_descriptions')
          .select('description_id, competency_id, description_level, description_name')
          .in('competency_id', allCompIds)
          .order('description_level');

        if (!descErr) {
          (descRows || []).forEach(row => {
            if (!descriptorMap[row.competency_id]) descriptorMap[row.competency_id] = {};
            if (!descriptorMap[row.competency_id][row.description_level]) {
              descriptorMap[row.competency_id][row.description_level] = [];
            }
            descriptorMap[row.competency_id][row.description_level].push({
              description_id: row.description_id,
              description_name: row.description_name,
            });
          });
        }
      }

      // ── Step 3: one item per descriptor (unique description_id as item_id) ─
      return groups.map(g => ({
        ...g,
        competencies: g.competencies.map(comp => ({
          ...comp,
          competency_items: [1, 2, 3, 4].flatMap(level =>
            (descriptorMap[comp.competency_id]?.[level] || []).map(d => ({
              item_id: d.description_id,
              level,
              description: d.description_name,
            }))
          ),
        })),
      }));
    } catch (error) {
      console.error('Error fetching competency data:', error);
      return [];
    }
  },

  async createEvaluation(evaluationData) {
    const { data, error } = await supabase
      .from('evaluations').insert([evaluationData]).select().single();
    if (error) { console.error('Error creating evaluation:', error); return { data: null, error }; }
    return { data, error: null };
  },

  async updateEvaluationStatus(evaluationId, status, completedAt = null) {
    try {
      const updateData = { status, updated_at: new Date().toISOString() };
      if (completedAt) updateData.completed_at = completedAt;
      const { data, error } = await supabase
        .from('evaluations').update(updateData).eq('evaluation_id', evaluationId).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating evaluation status:', error);
      return null;
    }
  },

  // Saves ratings to the appropriate table.
  // Key format: item_${competency_id}_level_${level}
  async saveRatings(evaluationId, ratings, competencyData, organizationId, evaluationType) {
    try {
      const isCore = isCoreEvalType(evaluationType);
      const table = isCore ? 'evaluation_core_ratings' : 'eval_func_ratings';

      await supabase.from(table).delete().eq('evaluation_id', evaluationId);

      // Build lookup: description_id (string) → { group_id, item_id }
      const itemLookup = {};
      (competencyData || []).forEach(group => {
        (group.competencies || []).forEach(comp => {
          (comp.competency_items || []).forEach(item => {
            itemLookup[String(item.item_id)] = {
              group_id: group.group_id,
              item_id: item.item_id,
            };
          });
        });
      });

      const ratingsArray = Object.entries(ratings)
        .map(([key, rating]) => {
          const match = key.match(/^item_(.+)_level_(\d+)$/);
          if (!match || !rating) return null;
          const itemKey = match[1];
          const level_number = parseInt(match[2], 10);
          const lookup = itemLookup[itemKey];
          if (!lookup) return null;

          if (isCore) {
            return {
              evaluation_id: evaluationId,
              group_id: parseInt(lookup.group_id, 10),
              competency_id: parseInt(lookup.item_id, 10),
              level_number,
              rating,
              organization_id: organizationId,
            };
          } else {
            return {
              evaluation_id: evaluationId,
              group_id: parseInt(lookup.group_id, 10),
              item_id: parseInt(lookup.item_id, 10),
              level_number,
              rating,
              organization_id: organizationId,
            };
          }
        })
        .filter(Boolean);

      if (ratingsArray.length === 0) return { success: true };
      const { error } = await supabase.from(table).insert(ratingsArray);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error saving ratings:', error);
      return { success: false, error };
    }
  },

  async getEvaluationById(id) {
    try {
      const { data: evaluation, error: evalError } = await supabase
        .from('evaluations')
        .select(`*, department:department_id(depart_name), role:role_id(role_title)`)
        .eq('evaluation_id', id).single();
      if (evalError) throw evalError;

      const isCore = isCoreEvalType(evaluation.evaluation_type);
      const table = isCore ? 'evaluation_core_ratings' : 'eval_func_ratings';
      const idField = isCore ? 'competency_id' : 'item_id';

      const { data: ratings, error: ratingsError } = await supabase
        .from(table).select(`${idField}, level_number, rating`).eq('evaluation_id', id);
      if (ratingsError) throw ratingsError;

      const ratingsObject = {};
      (ratings || []).forEach(r => {
        if (r[idField] && r.level_number) {
          ratingsObject[`item_${r[idField]}_level_${r.level_number}`] = r.rating;
        }
      });

      return { ...evaluation, ratings: ratingsObject };
    } catch (error) {
      console.error('Error fetching evaluation:', error);
      return null;
    }
  },

  async deleteEvaluation(id) {
    try {
      await Promise.all([
        supabase.from('evaluation_core_ratings').delete().eq('evaluation_id', id),
        supabase.from('eval_func_ratings').delete().eq('evaluation_id', id),
      ]);
      const { error } = await supabase.from('evaluations').delete().eq('evaluation_id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting evaluation:', error);
      return { success: false, error };
    }
  },
};

// ─── Delete confirmation modal ────────────────────────────────────────────────
const DeleteModal = ({ employeeName, onConfirm, onCancel }) => (
  <div className="tr-modal-overlay" onClick={onCancel}>
    <div className="tr-modal" onClick={e => e.stopPropagation()}>
      <div className="tr-modal-header">
        <h3 className="tr-modal-title">Delete Evaluation</h3>
        <button className="tr-modal-close" onClick={onCancel} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <p className="tr-modal-body">
        Are you sure you want to delete the evaluation for <strong>{employeeName}</strong>?
        This action cannot be undone.
      </p>
      <div className="tr-modal-actions">
        <button onClick={onCancel} className="tr-btn tr-btn-secondary">Cancel</button>
        <button onClick={onConfirm} className="tr-btn tr-btn-danger-solid">Delete</button>
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const TalentReviewSystem = () => {
  const { organizationId } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [currentEvaluationId, setCurrentEvaluationId] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    department_id: '',
    role_id: '',
    employeeName: '',
    evaluateAs: '',
    evaluationPeriod: '',
    evaluationType: '',
    ratings: {},
  });

  useEffect(() => { loadDashboardData(); }, [organizationId]); // eslint-disable-line

  const loadDashboardData = async () => {
    const [evals, depts] = await Promise.all([
      API.getEvaluations(organizationId),
      API.getDepartments(organizationId),
    ]);
    setEvaluations(evals);
    setDepartments(depts);
  };

  const handleNewEvaluation = () => {
    setFormData({ department_id: '', role_id: '', employeeName: '', evaluateAs: '', evaluationPeriod: '', evaluationType: '', ratings: {} });
    setCurrentEvaluationId(null);
    setCurrentPage('profile');
  };

  const handleViewEvaluation = async (evaluationId) => {
    const evaluation = await API.getEvaluationById(evaluationId);
    if (evaluation) {
      setCurrentEvaluationId(evaluationId);
      setFormData({
        department_id: evaluation.department_id,
        role_id: evaluation.role_id,
        employeeName: evaluation.employee_name,
        evaluateAs: evaluation.evaluator_role,
        evaluationPeriod: evaluation.evaluation_period,
        evaluationType: evaluation.evaluation_type,
        ratings: evaluation.ratings || {},
      });
      setCurrentPage('view');
    }
  };

  const handleContinueEvaluation = () => {
    if (isCoreEvalType(formData.evaluationType)) {
      setCurrentPage('core');
    } else {
      setCurrentPage('technical');
    }
  };

  const handleDeleteEvaluation = async (evaluationId) => {
    await API.deleteEvaluation(evaluationId);
    loadDashboardData();
  };

  const handleDownloadReport = async (evaluationId) => {
    const evaluation = await API.getEvaluationById(evaluationId);
    if (evaluation) {
      const groupType = isCoreEvalType(evaluation.evaluation_type) ? 'Core' : 'Technical';
      const competenciesData = await API.getCompetencyData(groupType, organizationId, evaluation.department_id);

      // Transform to the flat structure expected by the report generator:
      // group → one competency (group itself) → items (each actual competency)
      const reportData = competenciesData.map(group => ({
        group_id: group.group_id,
        group_name: group.group_name,
        competencies: [{
          competency_id: group.group_id,
          competency_name: group.group_name,
          competency_items: group.competencies.map(comp => ({
            item_id: comp.competency_id,
            description: comp.competency_name,
          })),
        }],
      }));

      const evaluationData = {
        employeeName: evaluation.employee_name,
        jobTitle: evaluation.role?.role_title || evaluation.job_title,
        department: evaluation.department?.depart_name || 'N/A',
        evaluationPeriod: evaluation.evaluation_period,
        evaluateAs: evaluation.evaluator_role,
        evaluationType: evaluation.evaluation_type,
      };

      generateEvaluationReport(evaluationData, evaluation.ratings || {}, reportData);
    }
  };

  const handleBackToDashboard = () => { setCurrentPage('dashboard'); loadDashboardData(); };

  const handleStartEvaluation = async () => {
    const evaluationData = {
      employee_name: formData.employeeName,
      department_id: parseInt(formData.department_id, 10),
      role_id: parseInt(formData.role_id, 10),
      evaluation_type: formData.evaluationType,
      evaluator_role: formData.evaluateAs,
      evaluation_period: formData.evaluationPeriod,
      status: 'Incomplete',
      organization_id: organizationId,
      created_at: new Date().toISOString(),
    };
    const { data: newEval, error } = await API.createEvaluation(evaluationData);
    if (newEval) {
      setCurrentEvaluationId(newEval.evaluation_id);
      if (isCoreEvalType(formData.evaluationType)) {
        setCurrentPage('core');
      } else {
        setCurrentPage('technical');
      }
    } else {
      alert(`Failed to create evaluation: ${error?.message || 'Please try again.'}`);
    }
  };

  const handleSaveAsDraft = async (ratings, competencyData) => {
    if (currentEvaluationId) {
      await API.saveRatings(currentEvaluationId, ratings, competencyData, organizationId, formData.evaluationType);
      await API.updateEvaluationStatus(currentEvaluationId, 'Incomplete');
      handleBackToDashboard();
    }
  };

  const handleSubmitEvaluation = async (ratings, competencyData) => {
    if (currentEvaluationId) {
      await API.saveRatings(currentEvaluationId, ratings, competencyData, organizationId, formData.evaluationType);
      await API.updateEvaluationStatus(currentEvaluationId, 'Complete', new Date().toISOString());
      alert('Evaluation submitted successfully!');
      handleBackToDashboard();
    }
  };

  return (
    <div className="tr-app">
      <div className="tr-container">
        <h1 className="tr-main-title"><i className="bi bi-clipboard-check" />TALENT REVIEW</h1>
        <p className="tr-subtitle">Employee Performance Evaluation System</p>

        {currentPage === 'dashboard' && (
          <Dashboard
            evaluations={evaluations}
            onNewEvaluation={handleNewEvaluation}
            onViewEvaluation={handleViewEvaluation}
            onDeleteEvaluation={handleDeleteEvaluation}
            onDownloadReport={handleDownloadReport}
          />
        )}

        {currentPage === 'profile' && (
          <ProfilePage
            formData={formData}
            setFormData={setFormData}
            departments={departments}
            onStart={handleStartEvaluation}
            onBack={handleBackToDashboard}
          />
        )}

        {currentPage === 'core' && (
          <EvaluationPage
            evaluationType="Core"
            formData={formData}
            setFormData={setFormData}
            evaluationId={currentEvaluationId}
            organizationId={organizationId}
            onBack={() => setCurrentPage('profile')}
            onSaveAsDraft={handleSaveAsDraft}
            onSubmit={handleSubmitEvaluation}
          />
        )}

        {currentPage === 'technical' && (
          <EvaluationPage
            evaluationType="Technical"
            formData={formData}
            setFormData={setFormData}
            evaluationId={currentEvaluationId}
            organizationId={organizationId}
            onBack={() => setCurrentPage('profile')}
            onSaveAsDraft={handleSaveAsDraft}
            onSubmit={handleSubmitEvaluation}
          />
        )}

        {currentPage === 'view' && (
          <ViewEvaluationPage
            formData={formData}
            evaluationId={currentEvaluationId}
            evaluations={evaluations}
            onBack={handleBackToDashboard}
            onDownload={() => handleDownloadReport(currentEvaluationId)}
            onContinue={handleContinueEvaluation}
          />
        )}
      </div>
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = ({ evaluations, onNewEvaluation, onViewEvaluation, onDeleteEvaluation, onDownloadReport }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);

  return (
    <>
      {deleteTarget && (
        <DeleteModal
          employeeName={deleteTarget.name}
          onConfirm={() => { onDeleteEvaluation(deleteTarget.id); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="tr-card">
        <p className="tr-dashboard-intro">
          Welcome to the Employee Evaluation Page. This module allows you to conduct comprehensive
          competency assessments through self-evaluations, peer reviews, and supervisor feedback.
        </p>
        <hr className="tr-divider" />
        <button onClick={onNewEvaluation} className="tr-btn tr-btn-primary tr-mb-3">
          New Evaluation
        </button>

        <div className="tr-table-container">
          <table className="tr-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Job Title</th>
                <th>Date</th>
                <th>Status</th>
                <th>Reviewer</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {evaluations.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No evaluations yet. Click "New Evaluation" to get started.
                  </td>
                </tr>
              ) : (
                evaluations.map((evaluation) => (
                  <tr key={evaluation.evaluation_id}>
                    <td>{evaluation.employee_name}</td>
                    <td>{evaluation.role?.role_title || evaluation.job_title || 'N/A'}</td>
                    <td>{new Date(evaluation.created_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`tr-badge ${evaluation.status === 'Complete' ? 'tr-badge-success' : 'tr-badge-warning'}`}>
                        {evaluation.status}
                      </span>
                    </td>
                    <td>{evaluation.evaluator_role}</td>
                    <td>
                      <div className="tr-action-buttons">
                        <button onClick={() => onViewEvaluation(evaluation.evaluation_id)} className="tr-btn tr-btn-sm tr-btn-dark" title="View Evaluation">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => onDownloadReport(evaluation.evaluation_id)} className="tr-btn tr-btn-sm tr-btn-dark" title="Download Report">
                          <Download size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget({ id: evaluation.evaluation_id, name: evaluation.employee_name })}
                          className="tr-btn tr-btn-sm tr-btn-danger"
                          title="Delete Evaluation"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ─── Profile Page ─────────────────────────────────────────────────────────────
const ProfilePage = ({ formData, setFormData, departments, onStart, onBack }) => {
  const { organizationId } = useAuth();
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  useEffect(() => {
    if (organizationId) {
      API.getCategories(organizationId).then(setCategories);
    }
  }, [organizationId]);

  useEffect(() => {
    if (formData.department_id) loadRoles(formData.department_id);
  }, [formData.department_id]);

  useEffect(() => {
    if (formData.role_id && roles.length > 0) {
      const role = roles.find(r => r.role_id === parseInt(formData.role_id));
      if (role) {
        const staticData = PEOPLE_CULTURE_ROLES[role.role_title];
        setSelectedRole(staticData ? { ...role, ...staticData } : role);
        setShowRoleInfo(true);
      }
    } else {
      setSelectedRole(null);
      setShowRoleInfo(false);
    }
  }, [formData.role_id, roles]);

  const loadRoles = async (departmentId) => {
    const rolesData = await API.getRolesByDepartment(departmentId);
    setRoles(rolesData);
  };

  const handleDepartmentChange = (e) => {
    setFormData({ ...formData, department_id: e.target.value, role_id: '' });
    setSelectedRole(null);
    setShowRoleInfo(false);
  };

  const canProceed = formData.department_id && formData.role_id && formData.employeeName &&
    formData.evaluateAs && formData.evaluationPeriod && formData.evaluationType;

  return (
    <div className="tr-card">
      <div className="tr-section">
        <h2 className="tr-section-title">A. Job Profile</h2>

        <div className="tr-grid-2">
          <div className="tr-form-group">
            <label className="tr-label">Department</label>
            <select value={formData.department_id} onChange={handleDepartmentChange} className="tr-select">
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>{dept.depart_name}</option>
              ))}
            </select>
          </div>

          <div className="tr-form-group">
            <label className="tr-label">Job Title</label>
            <select
              value={formData.role_id}
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              disabled={!formData.department_id}
              className="tr-select"
            >
              <option value="">Select Job Title</option>
              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>{role.role_title}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedRole && (
          <div className="tr-role-info-container">
            <div className="tr-role-info-header" onClick={() => setShowRoleInfo(!showRoleInfo)}>
              <h4 className="tr-role-info-title">Role Information</h4>
              {showRoleInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>

            {showRoleInfo && (
              <div className="tr-role-info-content">
                {selectedRole.purpose && (
                  <div className="tr-role-info-item">
                    <label className="tr-role-info-label">Job Purpose:</label>
                    <p className="tr-role-info-text">{parseText(selectedRole.purpose)}</p>
                  </div>
                )}

                {selectedRole.responsibilities && (() => {
                  const items = parseResponsibilities(selectedRole.responsibilities);
                  return items.length > 0 ? (
                    <div className="tr-role-info-item">
                      <label className="tr-role-info-label">Key Responsibilities:</label>
                      <ul className="tr-role-responsibilities-list">
                        {items.map((resp, idx) => <li key={idx}>{resp}</li>)}
                      </ul>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="tr-section">
        <h2 className="tr-section-title">B. Evaluation Information</h2>

        <div className="tr-grid-2">
          <div className="tr-form-group">
            <label className="tr-label">Employee Name</label>
            <input
              type="text"
              value={formData.employeeName}
              onChange={(e) => setFormData({ ...formData, employeeName: e.target.value })}
              placeholder="Enter employee name"
              className="tr-input"
            />
          </div>

          <div className="tr-form-group">
            <label className="tr-label">Competency Category</label>
            <select
              value={formData.evaluationType}
              onChange={(e) => setFormData({ ...formData, evaluationType: e.target.value })}
              className="tr-select"
            >
              <option value="">Select Category</option>
              {categories.length > 0 ? (
                categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_name}>{cat.category_name}</option>
                ))
              ) : (
                <>
                  <option value="Core Competency Evaluation">Core Competency Evaluation</option>
                  <option value="Technical Competency Evaluation">Technical Competency Evaluation</option>
                </>
              )}
            </select>
          </div>

          <div className="tr-form-group">
            <label className="tr-label">Evaluate As</label>
            <select
              value={formData.evaluateAs}
              onChange={(e) => setFormData({ ...formData, evaluateAs: e.target.value })}
              className="tr-select"
            >
              <option value="">Select Role</option>
              <option value="Self">Self</option>
              <option value="Peer">Peer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Direct Report">Direct Report</option>
            </select>
          </div>

          <div className="tr-form-group">
            <label className="tr-label">Evaluation Period</label>
            <select
              value={formData.evaluationPeriod}
              onChange={(e) => setFormData({ ...formData, evaluationPeriod: e.target.value })}
              className="tr-select"
            >
              <option value="">Select Period</option>
              <option value="Q1: January – March">Q1: January – March</option>
              <option value="Q2: April – June">Q2: April – June</option>
              <option value="Q3: July – September">Q3: July – September</option>
              <option value="Q4: October – December">Q4: October – December</option>
              <option value="Non-Routine">Non-Routine</option>
            </select>
          </div>
        </div>
      </div>

      <div className="tr-nav-buttons">
        <button onClick={onBack} className="tr-btn tr-btn-secondary">Back to Overview</button>
        <button onClick={onStart} disabled={!canProceed} className="tr-btn tr-btn-primary">Start</button>
      </div>
    </div>
  );
};

// ─── Evaluation Page (one group at a time) ────────────────────────────────────
const EvaluationPage = ({ evaluationType, formData, setFormData, evaluationId, organizationId, onBack, onSaveAsDraft, onSubmit }) => {
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [competencyData, setCompetencyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const topRef = useRef(null);

  const loadCompetencies = useCallback(async () => {
    setLoading(true);
    const data = await API.getCompetencyData(evaluationType, organizationId, formData.department_id);
    setCompetencyData(data);
    setCurrentGroupIndex(0);
    setLoading(false);
  }, [evaluationType, organizationId, formData.department_id]);

  useEffect(() => { loadCompetencies(); }, [loadCompetencies]);

  // Auto-save draft status as soon as the page is ready
  useEffect(() => {
    if (!loading && evaluationId) {
      API.updateEvaluationStatus(evaluationId, 'Incomplete');
    }
  }, [loading, evaluationId]);

  const scrollToTop = () => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const countRatings = (groups) => {
    let total = 0;
    let completed = 0;
    groups.forEach(group => {
      (group.competencies || []).forEach(comp => {
        (comp.competency_items || []).forEach(item => {
          total++;
          if (formData.ratings[`item_${item.item_id}_level_${item.level}`]) completed++;
        });
      });
    });
    return { total, completed };
  };

  const calculateProgress = () => {
    const { total, completed } = countRatings(competencyData);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getGroupStatus = (group) => {
    const { total, completed } = countRatings([group]);
    if (total === 0 || completed === 0) return 'Not Started';
    if (completed === total) return 'Completed';
    return `In Progress — ${Math.round((completed / total) * 100)}%`;
  };

  const isAllComplete = () => {
    const { total, completed } = countRatings(competencyData);
    return total > 0 && completed === total;
  };

  // Auto-save draft then advance to next group
  const handleNext = async () => {
    if (evaluationId) {
      await API.saveRatings(evaluationId, formData.ratings, competencyData, organizationId, formData.evaluationType);
    }
    setCurrentGroupIndex(prev => prev + 1);
    scrollToTop();
  };

  const handleSaveOrSubmit = () => {
    if (isAllComplete()) {
      onSubmit(formData.ratings, competencyData);
    } else {
      onSaveAsDraft(formData.ratings, competencyData);
    }
  };

  const progress = calculateProgress();
  const allComplete = isAllComplete();
  const currentGroup = competencyData[currentGroupIndex];
  const isLastGroup = !loading && competencyData.length > 0 && currentGroupIndex === competencyData.length - 1;

  return (
    <div className="tr-card" ref={topRef}>
      <div className="tr-section">
        <h2 className="tr-section-title">
          {evaluationType === 'Core' ? 'A. Core Competency Evaluation' : 'B. Technical Competency Evaluation'}
        </h2>
        <p className="tr-section-desc">
          {evaluationType === 'Core'
            ? 'Rate each competency descriptor across all four proficiency levels using the 1–5 scale.'
            : 'Assess technical proficiency for each competency descriptor using the 1–5 scale.'}
        </p>
        <div className="tr-progress-container">
          <div className="tr-progress-header">
            <span>Overall Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="tr-progress-bar">
            <div className="tr-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="tr-loading"><div className="tr-spinner" /><p>Loading competencies…</p></div>
      ) : competencyData.length === 0 ? (
        <div className="tr-empty-state">
          <p>No competency data found. Please ensure competencies and descriptors are configured in the Competency Bank.</p>
        </div>
      ) : (
        <>
          {/* Counter banner — "Group X of Y" only */}
          <div className="tr-group-counter">
            <span className="tr-group-counter-label">
              Group {currentGroupIndex + 1} of {competencyData.length}
            </span>
          </div>

          {/* Current group card */}
          <div className="tr-group-card tr-group-open">
            <div className="tr-group-header-static">
              <h3 className="tr-group-title">{currentGroupIndex + 1}. {currentGroup.group_name}</h3>
              <p className="tr-group-status">{getGroupStatus(currentGroup)}</p>
            </div>
            <div className="tr-group-content">
              {(currentGroup.competencies || []).map((comp, compIdx) => (
                <CompetencyEvaluator
                  key={comp.competency_id}
                  competency={comp}
                  index={compIdx}
                  formData={formData}
                  setFormData={setFormData}
                />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="tr-nav-buttons">
        <button onClick={onBack} className="tr-btn tr-btn-secondary">Back to Pre-Evaluation</button>

        {!loading && competencyData.length > 0 && (
          isLastGroup ? (
            <button
              onClick={handleSaveOrSubmit}
              className={`tr-btn ${allComplete ? 'tr-btn-primary' : 'tr-btn-draft'}`}
            >
              {allComplete ? 'Submit Evaluation' : 'Save as Draft'}
            </button>
          ) : (
            <button onClick={handleNext} className="tr-btn tr-btn-primary">
              Next
            </button>
          )
        )}
      </div>
    </div>
  );
};

// ─── Competency Evaluator (one competency accordion) ─────────────────────────
const CompetencyEvaluator = ({ competency, index, formData, setFormData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);

  const items = competency.competency_items || [];

  // Items visible for the currently selected level
  const levelItems = items.filter(item => item.level === currentLevel);

  const getRatingKey = (itemId, level) => `item_${itemId}_level_${level}`;

  const handleRatingChange = (itemId, level, rating) => {
    setFormData({ ...formData, ratings: { ...formData.ratings, [getRatingKey(itemId, level)]: rating } });
  };

  const getLevelTotal    = (lv) => items.filter(i => i.level === lv).length;
  const getLevelProgress = (lv) =>
    items.filter(i => i.level === lv && !!formData.ratings[getRatingKey(i.item_id, i.level)]).length;
  const isLevelComplete  = (lv) => { const t = getLevelTotal(lv); return t > 0 && getLevelProgress(lv) === t; };

  const levelsWithItems = [1, 2, 3, 4].filter(lv => getLevelTotal(lv) > 0);
  const isAllComplete   = levelsWithItems.length > 0 && levelsWithItems.every(lv => isLevelComplete(lv));

  const totalRated = items.filter(i => !!formData.ratings[getRatingKey(i.item_id, i.level)]).length;

  const statusText = items.length === 0
    ? 'No descriptors'
    : isAllComplete
      ? 'Complete'
      : `${totalRated}/${items.length} rated`;

  return (
    <div className="tr-competency-card">
      <div className="tr-competency-accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="tr-competency-header">
          <h4 className="tr-competency-title">
            {String.fromCharCode(65 + index)}. {competency.competency_name}
          </h4>
          <span className={`tr-competency-status ${isAllComplete ? 'tr-status-complete' : ''}`}>
            {statusText}
          </span>
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>

      {isOpen && (
        <div className="tr-competency-content">
          {/* 4-step level navigator */}
          <div className="tr-level-nav">
            <div className="tr-level-steps">
              {[1, 2, 3, 4].map(lv => (
                <React.Fragment key={lv}>
                  <div
                    onClick={() => setCurrentLevel(lv)}
                    className={`tr-level-step ${currentLevel === lv ? 'tr-level-active' : ''} ${isLevelComplete(lv) ? 'tr-level-complete' : ''}`}
                  >
                    {isLevelComplete(lv) ? <Check size={16} /> : lv}
                  </div>
                  {lv < 4 && <div className="tr-level-connector" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Current level label */}
          <div className="tr-current-level-info">
            <span className="tr-current-level-desc">Level {currentLevel}: {LEVEL_NAMES[currentLevel - 1]}</span>
          </div>

          {/* Descriptor rows for the current level — each in its own row */}
          <div className="tr-table-wrapper">
            <table className="tr-rating-table">
              <thead>
                <tr>
                  <th className="tr-rating-competence-col">Descriptor</th>
                  {RATING_SCALE.map(r => (
                    <th key={r.value} className="tr-rating-col">{r.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {levelItems.length > 0 ? (
                  levelItems.map(item => {
                    const currentRating = formData.ratings[getRatingKey(item.item_id, item.level)];
                    return (
                      <tr key={item.item_id}>
                        <td className="tr-rating-item">{item.description}</td>
                        {RATING_SCALE.map(r => (
                          <td key={r.value} className="tr-rating-cell">
                            <input
                              type="radio"
                              name={`rating-${competency.competency_id}-desc-${item.item_id}`}
                              checked={currentRating === r.value}
                              onChange={() => handleRatingChange(item.item_id, item.level, r.value)}
                              className="tr-radio"
                              aria-label={`Rating ${r.value}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={RATING_SCALE.length + 1} style={{ color: '#888', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                      No descriptors defined for Level {currentLevel}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Rating Scale Legend — horizontal */}
          <div className="tr-rating-legend">
            <span className="tr-rating-legend-title">Rating Scale:</span>
            <div className="tr-rating-legend-items">
              {RATING_SCALE.map(r => (
                <div key={r.value} className="tr-rating-legend-item">
                  <span className="tr-rating-legend-number">{r.value}</span>
                  <span className="tr-rating-legend-desc">{RATING_DESCRIPTIONS[r.value]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Level progress */}
          <div className="tr-level-progress">
            <span className="tr-level-progress-text">
              Level {currentLevel} — {getLevelProgress(currentLevel)}/{getLevelTotal(currentLevel)} descriptors rated
            </span>
          </div>

          {/* Previous / Continue buttons */}
          <div className="tr-level-nav-buttons">
            <button
              onClick={() => setCurrentLevel(lv => Math.max(1, lv - 1))}
              disabled={currentLevel === 1}
              className="tr-level-nav-btn tr-level-nav-btn-prev"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentLevel(lv => Math.min(4, lv + 1))}
              disabled={currentLevel === 4}
              className={`tr-level-nav-btn ${currentLevel < 4 ? 'tr-level-nav-btn-next' : 'tr-level-nav-btn-finish'}`}
            >
              {currentLevel < 4 ? 'Continue' : (isAllComplete ? 'Finished' : 'Complete All Levels')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── View Evaluation Page ─────────────────────────────────────────────────────
const ViewEvaluationPage = ({ formData, evaluationId, evaluations, onBack, onDownload, onContinue }) => {
  const currentEvaluation = evaluations.find(e => e.evaluation_id === evaluationId);
  const isIncomplete = currentEvaluation?.status === 'Incomplete';

  return (
    <div className="tr-card">
      <div className="tr-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 className="tr-section-title">Evaluation Details</h2>
            <p className="tr-section-desc">Review the evaluation</p>
          </div>
          <button onClick={onDownload} className="tr-btn tr-btn-primary">
            <Download size={16} style={{ marginRight: '0.5rem' }} />
            Download Report
          </button>
        </div>

        <div className="tr-grid-2">
          <div className="tr-form-group">
            <label className="tr-label">Employee Name</label>
            <div className="tr-view-field">{formData.employeeName}</div>
          </div>
          <div className="tr-form-group">
            <label className="tr-label">Competency Category</label>
            <div className="tr-view-field">{formData.evaluationType}</div>
          </div>
          <div className="tr-form-group">
            <label className="tr-label">Evaluated As</label>
            <div className="tr-view-field">{formData.evaluateAs}</div>
          </div>
          <div className="tr-form-group">
            <label className="tr-label">Evaluation Period</label>
            <div className="tr-view-field">{formData.evaluationPeriod}</div>
          </div>
          <div className="tr-form-group">
            <label className="tr-label">Status</label>
            <div className="tr-view-field">
              <span className={`tr-badge ${currentEvaluation?.status === 'Complete' ? 'tr-badge-success' : 'tr-badge-warning'}`}>
                {currentEvaluation?.status || 'Unknown'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="tr-nav-buttons">
        <button onClick={onBack} className="tr-btn tr-btn-secondary">Back to Dashboard</button>
        {isIncomplete && (
          <button onClick={onContinue} className="tr-btn tr-btn-primary">Continue Evaluation</button>
        )}
      </div>
    </div>
  );
};

export default TalentReviewSystem;
